import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Check, 
  Star, 
  Zap, 
  Users, 
  FolderOpen, 
  GitBranch, 
  HardDrive,
  CreditCard,
  Loader2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { createCheckoutSession, getPlanDetails, SUBSCRIPTION_PLANS } from '../../lib/stripe-checkout';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  credits: number;
  teamSeats: string | number;
  projects: string | number;
  versions: string | number;
  priceId: string;
}

interface SubscriptionPlansModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscriptionSuccess?: (planId: string) => void;
}

export function SubscriptionPlansModal({ isOpen, onClose, onSubscriptionSuccess }: SubscriptionPlansModalProps) {
  const { user } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use existing SUBSCRIPTION_PLANS from stripe-checkout.ts
      const plansList = Object.entries(SUBSCRIPTION_PLANS)
        .filter(([key]) => key !== 'trial') // Exclude trial from paid options
        .map(([key, plan]) => ({
          id: key,
          name: plan.name,
          price: plan.price,
          credits: plan.credits,
          teamSeats: plan.teamSeats,
          projects: plan.projects,
          versions: plan.versions,
          priceId: plan.priceId
        }));
      
      setPlans(plansList);
    } catch (error) {
      console.error('Error loading plans:', error);
      setError('Failed to load subscription plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = async (plan: SubscriptionPlan) => {
    if (!user?.id || processingCheckout) return;

    try {
      setSelectedPlan(plan.id);
      setProcessingCheckout(true);
      setError(null);

      console.log('🛒 Creating checkout for plan:', plan.name);

      const checkoutResult = await createCheckoutSession({
        userId: user.id,
        priceId: plan.priceId,
        planName: plan.name,
        userEmail: user.email
      });

      console.log('✅ Checkout session created, redirecting to:', checkoutResult.url);

      // Redirect to Stripe Checkout
      window.location.href = checkoutResult.url;

    } catch (error) {
      console.error('Error creating checkout:', error);
      setError(error instanceof Error ? error.message : 'Failed to create checkout session');
      setSelectedPlan(null);
      setProcessingCheckout(false);
    }
  };

  const getPlanFeatures = (plan: SubscriptionPlan): string[] => {
    return [
      `${plan.credits.toLocaleString()} AI analyses per month`,
      `${plan.teamSeats} team seats`,
      `${plan.projects} projects`,
      `${plan.versions} versions per creative`,
      `Priority support`,
      `Advanced analytics`
    ];
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="flex min-h-screen items-start justify-center p-4 py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[85vh] overflow-hidden flex flex-col my-auto"
          >
            {/* Header - Fixed */}
            <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-6 text-white flex-shrink-0">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                disabled={processingCheckout}
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-2">Choose Your Plan</h2>
                <p className="text-purple-100">
                  Upgrade your account to unlock powerful AI analysis features
                </p>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-8">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  <span className="ml-3 text-gray-600">Loading plans...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {plans.map((plan) => {
                    const isSelected = selectedPlan === plan.id;
                    const isProcessing = processingCheckout && isSelected;
                    const features = getPlanFeatures(plan);

                    return (
                      <motion.div
                        key={plan.id}
                        whileHover={{ scale: 1.02 }}
                        className={`relative bg-white rounded-xl border-2 p-6 transition-all cursor-pointer ${
                          plan.name === 'Pro' 
                            ? 'border-purple-300 shadow-lg ring-2 ring-purple-100' 
                            : 'border-gray-200 hover:border-purple-200 hover:shadow-md'
                        } ${
                          isSelected ? 'ring-2 ring-purple-500' : ''
                        }`}
                        onClick={() => !processingCheckout && handlePlanSelect(plan)}
                      >
                        {plan.name === 'Pro' && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                            <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1 rounded-full text-xs font-medium flex items-center">
                              <Star className="w-3 h-3 mr-1" />
                              Most Popular
                            </span>
                          </div>
                        )}

                        {/* Plan Header */}
                        <div className="text-center mb-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                          
                          <div className="mb-4">
                            <span className="text-4xl font-bold text-gray-900">
                              ${plan.price}
                            </span>
                            <span className="text-gray-600 ml-2">/month</span>
                          </div>
                        </div>

                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 gap-3 mb-6 text-xs">
                          <div className="text-center p-2 bg-blue-50 rounded-lg">
                            <Zap className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                            <div className="font-semibold text-gray-900">{plan.credits}</div>
                            <div className="text-gray-600">Credits</div>
                          </div>
                          <div className="text-center p-2 bg-green-50 rounded-lg">
                            <Users className="w-4 h-4 text-green-600 mx-auto mb-1" />
                            <div className="font-semibold text-gray-900">{plan.teamSeats}</div>
                            <div className="text-gray-600">Team Seats</div>
                          </div>
                          <div className="text-center p-2 bg-purple-50 rounded-lg">
                            <FolderOpen className="w-4 h-4 text-purple-600 mx-auto mb-1" />
                            <div className="font-semibold text-gray-900">{plan.projects}</div>
                            <div className="text-gray-600">Projects</div>
                          </div>
                          <div className="text-center p-2 bg-orange-50 rounded-lg">
                            <HardDrive className="w-4 h-4 text-orange-600 mx-auto mb-1" />
                            <div className="font-semibold text-gray-900">5GB</div>
                            <div className="text-gray-600">Storage</div>
                          </div>
                        </div>

                        {/* Features */}
                        <div className="space-y-2 mb-6">
                          {features.slice(0, 5).map((feature, index) => (
                            <div key={index} className="flex items-start text-sm">
                              <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>

                        {/* CTA Button */}
                        <button
                          className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center ${
                            plan.name === 'Pro'
                              ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } ${
                            processingCheckout ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={processingCheckout}
                        >
                          {isProcessing ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Select Plan
                            </>
                          )}
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Footer */}
              <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-600">
                <p>
                  All plans include 30-day money-back guarantee • Cancel anytime • Secure payment by Stripe
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}

export default SubscriptionPlansModal;